import { backendUrl, SESSION_TOKEN_HEADER, USER_NAME_HEADER } from '@common/contracts';
import axios from 'axios';
import { getIdentity } from '../auth/identity';
import { getSessionToken } from '../auth/session';
// Base API URL
const API_BASE_URL = `${backendUrl}/api`;
// Create a configured axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000, // 15 second timeout
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
});
// Request interceptor: attach the login session token, plus the username (a
// dev/test fallback the backend only trusts outside production). The client no
// longer sends a role header — admin status is derived server-side from the DB.
axiosInstance.interceptors.request.use((config) => {
    // A file upload is FormData, and the instance's default Content-Type would
    // send it labelled application/json with no multipart boundary — the server
    // then sees no file at all and answers "No file was uploaded" before a byte of
    // the body matters. Dropping the header lets the browser write the real one,
    // boundary included. Callers used to pass 'multipart/form-data' by hand at
    // every upload site; now forgetting it cannot break anything.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        delete config.headers['Content-Type'];
    }
    const token = getSessionToken();
    if (token) {
        config.headers[SESSION_TOKEN_HEADER] = token;
    }
    const identity = getIdentity();
    if (identity.username) {
        config.headers[USER_NAME_HEADER] = identity.username;
    }
    return config;
});
// Response interceptor: unwrap data and log failures (successes are not logged)
axiosInstance.interceptors.response.use((response) => {
    return response.data; // Return only the data, without the AxiosResponse wrapper
}, (error) => {
    var _a, _b, _c, _d, _e, _f;
    // Extended error logging
    if (error.response) {
        // The server responded with an error code
        console.error(`API Error ${error.response.status}: ${(_b = (_a = error.config) === null || _a === void 0 ? void 0 : _a.method) === null || _b === void 0 ? void 0 : _b.toUpperCase()} ${(_c = error.config) === null || _c === void 0 ? void 0 : _c.url}`, error.response.data);
    }
    else if (error.request) {
        // The request was made but no response was received (e.g. the server is unavailable)
        console.error(`API No Response: ${(_e = (_d = error.config) === null || _d === void 0 ? void 0 : _d.method) === null || _e === void 0 ? void 0 : _e.toUpperCase()} ${(_f = error.config) === null || _f === void 0 ? void 0 : _f.url}`, error.request);
    }
    else {
        // Something went wrong while setting up the request
        console.error('API Error Setup:', error.message);
    }
    return Promise.reject(error);
});
// Typed API client methods
export const client = {
    get: (url, config) => axiosInstance.get(url, config),
    post: (url, data, config) => axiosInstance.post(url, data, config),
    put: (url, data, config) => axiosInstance.put(url, data, config),
    patch: (url, data, config) => axiosInstance.patch(url, data, config),
    delete: (url, config) => axiosInstance.delete(url, config),
};
/**
 * Pull the server's own explanation out of a rejected request. The response
 * interceptor rejects with the raw AxiosError, so `{ error }` bodies (which every
 * upload route returns) would otherwise be swallowed by a generic catch — and the
 * caller would show "Upload failed" instead of "Wrong image size: 1024x1024 …".
 */
export function serverErrorMessage(err, fallback) {
    var _a, _b;
    const data = (_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.data;
    if (data && typeof data === 'object') {
        const msg = (_b = data.error) !== null && _b !== void 0 ? _b : data.message;
        if (typeof msg === 'string' && msg.trim())
            return msg;
    }
    return fallback;
}
